import React, { useState } from 'react';
import { 
  Heart, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  Crown, 
  Bookmark, 
  Eye, 
  Users, 
  ChevronLeft, 
  ChevronRight 
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { API_URL } from '../lib/api-client';

export interface ProfileCardProps {
  profile: any;
  onViewProfile?: (profile: any) => void;
  onSendInterest?: (profileId: string, e: React.MouseEvent) => void;
  onShortlist?: (profileId: string, e: React.MouseEvent) => void;
  isShortlisted?: boolean;
  isSentInterest?: boolean;
  isLoadingInterest?: boolean;
  className?: string;
}

const calculateAge = (dateOfBirth: string) => {
  try {
    if (!dateOfBirth) return 25;
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  } catch {
    return 25;
  }
};

const getInitials = (name: string) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
};

const extractProfilePhotos = (profile: any): string[] => {
  if (!profile) return [];
  const photos: string[] = [];

  const addPhoto = (item: any) => {
    if (!item) return;
    if (typeof item === 'string' && item.trim()) {
      const url = item.startsWith('http') || item.startsWith('data:')
        ? item 
        : `${API_URL.replace('/api', '')}/uploads/${item}`;
      if (!photos.includes(url)) photos.push(url);
    } else if (typeof item === 'object' && item?.url) {
      const url = item.url.startsWith('http') || item.url.startsWith('data:')
        ? item.url 
        : `${API_URL.replace('/api', '')}/uploads/${item.url}`;
      if (!photos.includes(url)) photos.push(url);
    }
  };

  // Priority 1: profilePhoto & userId.profilePhoto
  addPhoto(profile.profilePhoto);
  addPhoto(profile.userId?.profilePhoto);

  // Priority 2: photos array at root or inside userId
  if (Array.isArray(profile.photos)) {
    profile.photos.forEach(addPhoto);
  }
  if (Array.isArray(profile.userId?.photos)) {
    profile.userId.photos.forEach(addPhoto);
  }

  // Priority 3: legacy fields
  addPhoto(profile.photo);
  addPhoto(profile.image);
  addPhoto(profile.avatar);

  return photos;
};

export function ProfileCard({ 
  profile, 
  onViewProfile,
  onSendInterest,
  onShortlist,
  isShortlisted = false,
  isSentInterest = false,
  isLoadingInterest = false,
  className = ''
}: ProfileCardProps) {
  const [photoIndex, setPhotoIndex] = useState(0);

  const profileId = profile?._id || profile?.userId?._id || profile?.id || '';
  const displayName = profile?.name || profile?.displayName || profile?.basicInfo?.fullName || 
    (profile?.userId?.firstName ? `${profile.userId.firstName} ${profile.userId.lastName || ''}`.trim() : 'User Profile');
  
  const age = profile?.age || (profile?.dateOfBirth || profile?.basicInfo?.dateOfBirth ? calculateAge(profile.dateOfBirth || profile.basicInfo?.dateOfBirth) : 25);
  const height = profile?.height || profile?.physicalAttributes?.height || "5'5\"";
  
  // Extract all photo URLs using multi-priority helper
  const profilePhotos = extractProfilePhotos(profile);
  const currentPhoto = profilePhotos[photoIndex] || profilePhotos[0];
  const hasMultiplePhotos = profilePhotos.length > 1;
  const isPremium = profile?.isPremium || profile?.activePlan || profile?.isPro;
  const isOnline = profile?.isOnline;

  const handlePhotoNav = (direction: 'prev' | 'next', e: React.MouseEvent) => {
    e.stopPropagation();
    if (direction === 'next') {
      setPhotoIndex(prev => (prev + 1) % profilePhotos.length);
    } else {
      setPhotoIndex(prev => (prev - 1 + profilePhotos.length) % profilePhotos.length);
    }
  };

  const getEducationDisplay = () => {
    if (!profile?.education) return 'Not specified';
    if (typeof profile.education === 'string') return profile.education;
    if (typeof profile.education === 'object') {
      const edu: any = profile.education;
      const parts: string[] = [];
      if (edu.level) parts.push(edu.level);
      if (edu.field) parts.push(edu.field);
      return parts.length > 0 ? parts.join(' - ') : 'Not specified';
    }
    return 'Not specified';
  };

  return (
    <Card 
      className={`group hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-200 rounded-2xl bg-white flex flex-col justify-between shadow-sm hover:-translate-y-1 ${className}`}
      onClick={() => onViewProfile && onViewProfile(profile)}
    >
      <div>
        {/* Profile Image Box */}
        <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
          {currentPhoto ? (
            <ImageWithFallback
              src={currentPhoto}
              alt={displayName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-100 to-purple-100">
              <span className="text-5xl font-bold text-rose-300">{getInitials(displayName)}</span>
            </div>
          )}

          {/* Premium Badge */}
          {isPremium && (
            <div className="absolute top-3 left-3 z-10">
              <span className="bg-amber-500 text-white text-xs px-2.5 py-1 rounded-full font-bold shadow-md inline-flex items-center gap-1">
                <Crown className="size-3" />
                Premium
              </span>
            </div>
          )}

          {/* Online Status */}
          {isOnline && (
            <div className="absolute top-3 right-3 z-10">
              <span className="bg-emerald-500 text-white text-xs px-2.5 py-1 rounded-full font-semibold shadow-md inline-flex items-center gap-1.5">
                <span className="size-2 bg-white rounded-full animate-pulse" />
                Online
              </span>
            </div>
          )}

          {/* Photo Navigation Arrows */}
          {hasMultiplePhotos && (
            <>
              <button
                onClick={(e) => handlePhotoNav('prev', e)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <ChevronLeft className="size-4 text-gray-800" />
              </button>
              <button
                onClick={(e) => handlePhotoNav('next', e)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
              >
                <ChevronRight className="size-4 text-gray-800" />
              </button>
            </>
          )}

          {/* Photo Indicators/Dots */}
          {hasMultiplePhotos && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {profilePhotos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPhotoIndex(idx);
                  }}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === photoIndex 
                      ? 'bg-white w-4' 
                      : 'bg-white/60 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Quick Actions Hover Overlay */}
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              size="sm"
              className="bg-white text-gray-900 hover:bg-gray-100 font-semibold shadow-lg text-xs"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewProfile) onViewProfile(profile);
              }}
            >
              <Eye className="size-3.5 mr-1" />
              View Profile
            </Button>
          </div>
        </div>

        {/* Profile Info Section */}
        <div className="p-4 space-y-2">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1 pr-2">
              <h3 className="font-bold text-base text-gray-900 group-hover:text-rose-600 transition-colors truncate">
                {displayName}
              </h3>
              <p className="text-xs text-gray-500 font-medium mt-0.5">
                {age} yrs • {height}
              </p>
            </div>
            {onShortlist && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShortlist(profileId, e);
                }}
                className="p-1.5 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors flex-shrink-0"
              >
                <Bookmark className={`size-4 ${isShortlisted ? 'fill-rose-600 text-rose-600' : ''}`} />
              </button>
            )}
          </div>

          <div className="space-y-1 text-xs text-gray-600 pt-1">
            {profile?.religion && (
              <div className="flex items-center gap-1.5">
                <Users className="size-3.5 text-rose-500 flex-shrink-0" />
                <span className="truncate">{profile.religion}</span>
              </div>
            )}
            {profile?.education && (
              <div className="flex items-center gap-1.5">
                <GraduationCap className="size-3.5 text-rose-500 flex-shrink-0" />
                <span className="truncate">{getEducationDisplay()}</span>
              </div>
            )}
            {profile?.occupation && (
              <div className="flex items-center gap-1.5">
                <Briefcase className="size-3.5 text-rose-500 flex-shrink-0" />
                <span className="truncate">{profile.occupation}</span>
              </div>
            )}
            {(profile?.city || profile?.location) && (
              <div className="flex items-center gap-1.5">
                <MapPin className="size-3.5 text-rose-500 flex-shrink-0" />
                <span className="truncate">{profile.city || profile.location}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="p-4 pt-0">
        <Button
          size="sm"
          className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-bold text-xs h-9 rounded-xl shadow-sm disabled:opacity-60"
          onClick={(e) => {
            e.stopPropagation();
            if (onSendInterest) onSendInterest(profileId, e);
          }}
          disabled={isLoadingInterest || isSentInterest}
        >
          {isLoadingInterest ? (
            <div className="flex items-center gap-1.5">
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : isSentInterest ? (
            <>Connect Sent</>
          ) : (
            <>
              <Heart className="size-3.5 mr-1.5 fill-white" />
              Send Connect Interest
            </>
          )}
        </Button>
      </div>
    </Card>
  );
}

export default ProfileCard;